import { useRouter } from 'next/router'
import { useContext, useState, useEffect, useMemo, useCallback } from 'react'
import { AuthContext } from '../../contexts/AuthContext'
import getPagination, {
  paginationType,
} from '../../utilities/functions/getPagination'
import useUpdatedItems from '../../hooks/useUpdatedItems'
import formatGetUrl from '../../utilities/functions/formatGetUrl'
import _isEqual from 'lodash/isEqual'

type queryType = {
  itemConditionIds?: string
  itemSearchKeywords?: string
  pageId: string
  auctionId: string
}

export type filterStateType = {
  auctionId: number
  pageId: number
  itemConditionIds: number[]
  itemSearchKeywords?: string
}

const useFilter = ({
  initialItems,
  initialPagination,
  isRefreshDisabled,
}: {
  initialItems: any[]
  initialPagination: paginationType
  isRefreshDisabled: boolean
}): {
  items: any[]
  filter: filterStateType
  pagination: paginationType
  isLoading: boolean
  handlers: any
} => {
  const { instance } = useContext<any>(AuthContext)
  const { query, push }: any = useRouter()

  const [filter, setFilter] = useState<filterStateType>(
    queryToFilterState(query)
  )
  const [pagination, setPagination] =
    useState<paginationType>(initialPagination)
  const [items, setItems, { setFetchUrl, loading }] = useUpdatedItems({
    url: formatGetUrl(filterStateToQuery({ ...filter })),
    initialData: initialItems,
    isRefreshDisabled: isRefreshDisabled,
  })

  const updatePagination = (
    name: 'current_page' | 'per_page' | 'total' | 'total_pages',
    value: any
  ) => {
    setPagination((prevState) => ({ ...prevState, [name]: value }))
  }

  const refreshPagination = async (newFilter: filterStateType) => {
    const queryString = Object.entries(newFilter).reduce(
      (acc: string, [key, value]) => (!value ? acc : `${acc}&${key}=${value}`),
      ''
    )
    const { data } = await instance.get(
      `/item/getItemsCountByAuctionId/${newFilter.auctionId}?${queryString}`
    )
    setPagination(getPagination(data, newFilter.pageId))
  }

  const handlers = useMemo(
    () => ({
      condition: (id: number, isChecked: boolean) => {
        setFilter((prevState) => {
          const newIds = prevState.itemConditionIds.filter(
            (conditionId) => conditionId !== id
          )
          return {
            ...prevState,
            itemConditionIds: isChecked ? [...newIds, id] : newIds,
            pageId: 1,
          }
        })
        updatePagination('current_page', 1)
      },
      pageChange: (page: number) => {
        updatePagination('current_page', page)
        setFilter((prevState) => ({ ...prevState, pageId: page }))
      },
      searchInput: (value: string) => {
        if (value === filter.itemSearchKeywords) return
        setFilter((prevState) => ({
          ...prevState,
          itemSearchKeywords: value,
          pageId: 1,
        }))
        updatePagination('current_page', 1)
      },
      clearAllFilters: () => {
        const newFilter = {
          itemConditionIds: [],
          pageId: 1,
          itemSearchKeywords: '',
          auctionId: Number(query.auctionId),
        }
        setFilter(newFilter)
        updatePagination('current_page', 1)
      },
    }),
    [query.auctionId, filter.itemSearchKeywords]
  )

  useEffect(() => {
    const formatQuery = queryToFilterState(query)
    if (!_isEqual(formatQuery, filter)) {
      setFilter(formatQuery)
      setFetchUrl(formatGetUrl(filterStateToQuery({ ...filter })))
    }
  }, [query])

  useEffect(() => {
    const formattedQuery = filterStateToQuery(filter)
    const { query } = formattedQuery
    setFetchUrl(formatGetUrl(filterStateToQuery({ ...filter })))
    push(
      { pathname: '/auction-detail/[auctionId]/[pageId]', query },
      undefined,
      { shallow: true }
    )
  }, [filter])

  useEffect(() => {
    refreshPagination(filter)
  }, [filter])

  return {
    items,
    filter,
    pagination,
    isLoading: loading,
    handlers,
  }
}

export const queryToFilterState = (query: queryType): filterStateType => {
  const formattedQuery: filterStateType = {
    itemConditionIds: [],
    pageId: 1,
    itemSearchKeywords: '',
    auctionId: Number(query.auctionId),
  }
  if (query.itemConditionIds) {
    formattedQuery.itemConditionIds = query.itemConditionIds
      .split(',')
      .map((n) => Number(n))
  }
  if (query.itemSearchKeywords) {
    formattedQuery.itemSearchKeywords = query.itemSearchKeywords
  }
  formattedQuery.pageId = Number(query.pageId) || 1
  return formattedQuery
}

export const filterStateToQuery = ({
  auctionId,
  pageId,
  itemSearchKeywords,
  itemConditionIds,
}: filterStateType) => {
  const query: {
    pageId: number
    auctionId: number
    itemSearchKeywords?: string
    itemConditionIds?: string
  } = {
    pageId,
    auctionId,
  }

  if (itemSearchKeywords) {
    query.itemSearchKeywords = itemSearchKeywords
  }
  if (itemConditionIds.length) {
    query.itemConditionIds = itemConditionIds.join(',')
  }

  return {
    path: `/item/getItemsByAuctionId/${auctionId}`,
    query,
  }
}

export const getQueryString = (query: any) =>
  Object.entries(query)
    .map(filterQueryItem)
    .reduce(
    (acc: string, [key, value]) => (!value ? acc : `${acc}&${key}=${value}`),
    ''
  )

const filterQueryItem = ([key, value]: any) => {
  if (key === 'pageId') {
    return [key, Number(value) || 1]
  }
  return [key, value]
}

export default useFilter
